<template>
  <div class="buildings-choose__wrap">
    <div ref="container" class="buildings-choose">
      <div class="background">
        <img
          loading="lazy"
          :src="genplanImage"
          :alt="genplanName"
          @load="mainImageLoaded = true"
        />
      </div>
      <div v-if="hasBuildings && mainImageLoaded" class="tooltip__wrap">
        <svg
          ref="area"
          class="tooltip__area"
          :viewBox="`0 0 ${viewBoxWidth} ${viewBoxHeight}`"
          @mousemove="onMousemove"
          @click="onBuildingClick"
          @touchend="onBuildingClick"
        >
          <path
            v-for="building in buildingsData"
            :key="`${building.id}-${building.isActive}`"
            :d="building.pathData"
            :data-id="building.id"
            :class="{
              active: building.isActive,
            }"
          />
        </svg>
        <Item
          v-for="building in buildingsData"
          :key="`popup-${building.id}`"
          :dot="building"
          :class="{
            active: building.isActive,
            'with-text-hover': building.tooltipText,
          }"
          @close="closePopup(building)"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, inject, watch } from "vue";
import { gsap } from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import Item from "@/components/planArchive/Item.vue";

gsap.registerPlugin(ScrollToPlugin);

const mq = inject("mq");
const genplanStore = inject("genplanStore");

const mainImageLoaded = ref(false);
const viewBoxWidth = ref(6000);
const viewBoxHeight = ref(3374);
const area = ref(null);
const container = ref(null);

const buildingsState = ref({});
let lastActiveId = null;

const genplanData = computed(() => genplanStore?.state.data?.genplan || {});
const genplanImage = computed(() => genplanData.value.image?.source || "");
const genplanName = computed(() => genplanData.value.name || "Генплан");
const buildingsList = computed(() => genplanStore?.state.data?.list || []);
const hasBuildings = computed(() => buildingsList.value.length > 0);

watch(
  buildingsList,
  (newList) => {
    const newState = {};
    newList.forEach((building) => {
      if (building?.area_points?.[0] && building?.building) {
        newState[building.building] = {
          isActive: false,
          position: building.position || { x: 0, y: 0 },
        };
      }
    });
    buildingsState.value = newState;
  },
  { immediate: true }
);

const buildingsData = computed(() => {
  const result = [];

  const groupedBuildings = {};

  buildingsList.value.forEach((building) => {
    if (!building?.area_points?.[0]) return;
    const key = building.building;
    if (!groupedBuildings[key]) {
      groupedBuildings[key] = {
        building: key,
        sections: [],
        allFloors: [],
        area_points: building.area_points,
      };
    }
    groupedBuildings[key].sections.push(building.section);
    groupedBuildings[key].allFloors.push(...building.floors);
  });

  Object.values(groupedBuildings).forEach((group) => {
    const state = buildingsState.value[group.building] || {};

    const uniqueFloors = [];
    const floorMap = new Map();
    group.allFloors.forEach((floor) => {
      if (!floorMap.has(floor.floor)) {
        floorMap.set(floor.floor, floor);
        uniqueFloors.push(floor);
      }
    });

    uniqueFloors.sort((a, b) => a.floor - b.floor);

    result.push({
      id: group.building,
      title: `Корпус ${group.building}`,
      pathData: convertPointsToPath(group.area_points[0]),
      isActive: state.isActive || false,
      isAvailable: true,
      floors: uniqueFloors,
      section: group.sections.join(", "),
      tooltipText: `${group.building} корпус`,
      text:
        group.building === "2.1" || group.building === "2.2-2.3"
          ? "Старт продаж!"
          : "",
    });
  });
  return result;
});

function convertPointsToPath(pointsStr) {
  if (!pointsStr) return "";

  try {
    const scaleX = viewBoxWidth.value / 6000;
    const scaleY = viewBoxHeight.value / 3374;

    return (
      pointsStr
        .split(" ")
        .map((pair) => {
          const [x, y] = pair.split(",");
          return `${(parseFloat(x) || 0) * scaleX},${(parseFloat(y) || 0) * scaleY}`;
        })
        .join(" L")
        .replace(/^/, "M") + " Z"
    );
  } catch (error) {
    console.error("Error converting points to path:", error);
    return "";
  }
}

function onMousemove({ target }) {
  const id = target?.dataset?.id;

  if (id !== lastActiveId) {
    if (lastActiveId && buildingsState.value[lastActiveId]) {
      buildingsState.value = {
        ...buildingsState.value,
        [lastActiveId]: {
          ...buildingsState.value[lastActiveId],
          isActive: false,
        },
      };
    }

    if (id) {
      buildingsState.value = {
        ...buildingsState.value,
        [id]: {
          ...buildingsState.value[id],
          isActive: true,
        },
      };
    }

    lastActiveId = id;
  }
}

function onBuildingClick(event) {
  event.stopPropagation();
  const id = event.target?.dataset?.id;
  if (!id) return;

  const building = buildingsData.value.find((b) => b.id === id);
  if (!building) return;

  scrollToBuilding(building);
}

function scrollToBuilding(building) {
  if (!container.value) return;

  const pathElement = area.value.querySelector(
    `path[data-id="${building.id}"]`
  );
  if (!pathElement) return;

  const bbox = pathElement.getBBox();
  const centerX = bbox.x + bbox.width / 2;
  const centerY = bbox.y + bbox.height / 2;

  const scale = container.value.scrollWidth / viewBoxWidth.value;
  const leftOffset = -200;

  const scrollLeft =
    (centerX + leftOffset) * scale - container.value.clientWidth / 2;
  const scrollTop = centerY * scale - container.value.clientHeight / 2;

  if (window.innerWidth <= 1120 || navigator.maxTouchPoints > 0) {
    gsap.to(container.value, {
      scrollTo: { x: scrollLeft, y: scrollTop },
      duration: 0.3,
      ease: "power2.out",
    });
  } else {
    container.value.scrollTo({
      left: scrollLeft,
      top: scrollTop,
      behavior: "smooth",
    });
  }
}

function scrollToPosition(leftPosition) {
  if (!container.value) return;

  container.value.scrollTo({
    left: leftPosition,
    behavior: "smooth",
  });
}

onMounted(() => {
  scrollToPosition(350);
});

function closePopup(building) {}
</script>

<style scoped>
@keyframes swipe {
  0% {
    transform: translateX(160px);
  }

  20% {
    transform: translateX(160px);
  }

  100% {
    transform: translateX(0);
  }
}

@media (max-width: 1120px) {
  .background,
  .tooltip__wrap {
    width: 1120px;
    height: 630px;
    padding: 0;
  }
}

@media (pointer: fine) {
  .swiper-helper {
    display: none !important;
  }
}

@media (min-width: 1121px) {
  .swiper-helper {
    display: none !important;
  }
}

.buildings-choose__wrap {
  position: relative;
}

.buildings-choose {
  position: relative;
  z-index: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

.background {
  position: relative;
  z-index: -1;
  padding-bottom: 56.2333%;
  background: #b6acaa;
}

.background::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 33%;
  background: linear-gradient(
    0deg,
    rgb(63 68 64 / 0) 0%,
    rgb(63 68 64 / 0.5) 100%
  );
  pointer-events: none;
}

.background::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 100%;
  height: 33%;
  background: linear-gradient(
    180deg,
    rgb(63 68 64 / 0) 0%,
    rgb(63 68 64 / 0.5) 100%
  );
  pointer-events: none;
}

.background img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  max-width: none;
  height: auto;
}

.tooltip__dot:nth-of-type(1) {
  top: 60%;
  left: 74%;
}

.tooltip__dot:nth-of-type(2) {
  top: 71%;
  left: 46.5%;
}

.tooltip__dot:nth-of-type(3) {
  top: 71%;
  left: 64%;
}

.tooltip__dot:nth-of-type(4) {
  top: 60%;
  left: 74%;
}

.tooltip__dot:nth-of-type(5) {
  top: 71.5%;
  left: 50.5%;
}

.tooltip__popup {
  cursor: pointer;
}

.tooltip__area path,
.tooltip__area rect,
.tooltip__area polygon {
  cursor: default;
  pointer-events: auto !important;
}

body.-notouch .tooltip__area path.active,
body.-notouch .tooltip__area rect.active,
body.-notouch .tooltip__area polygon.active {
  opacity: 0.6;
}

body.-notouch .tooltip__area path.unavailable,
body.-notouch .tooltip__area rect.unavailable,
body.-notouch .tooltip__area polygon.unavailable {
  cursor: pointer;
}

.swiper-helper {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  overflow: hidden;
  width: 100%;
  height: 150px;
  background: linear-gradient(
    0deg,
    rgb(255 255 255 / 0) 0%,
    rgb(255 255 255 / 0.7) 50%,
    rgb(255 255 255 / 1) 100%
  );
  pointer-events: none;
}

.swiper-helper__inner {
  position: absolute;
  top: 30px;
  left: 50%;
  width: 190px;
  height: 30px;
  margin-left: -95px;
}

.swiper-helper__inner::after {
  content: "";
  display: block;
  width: 30px;
  height: 30px;
  border: 5px solid #509c3b;
  border-radius: 50%;
  animation: swipe 1.25s ease-out infinite;
}

.purchase__nav {
  position: absolute;
  right: 0;
  bottom: 28px;
  left: 0;
  margin: 0;
  padding: 0 15px;
  list-style: none;
}

.purchase__nav li {
  margin: 0 8px;
  padding: 0;
}

@media (max-width: 991px) {
  .purchase__nav {
    bottom: 20px;
  }

  .purchase__nav li {
    margin: 0 4px 8px;
  }

  .purchase__nav .btn {
    padding: 7px 15px 10px !important;
    font-size: 18px !important;
  }
}

@media (max-width: 767px) {
  .purchase__nav {
    bottom: 10px;
  }
}

@media (max-width: 374px) {
  .purchase__nav li {
    margin: 0 2px 8px;
  }

  .purchase__nav .btn {
    padding-right: 13px !important;
    padding-left: 13px !important;
  }
}
</style>
